import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api, { getImageUrl } from '../api';

const STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
const STEP_LABELS = {
  pending:    { label: 'Order Placed',  icon: '🛒' },
  confirmed:  { label: 'Confirmed',     icon: '✅' },
  processing: { label: 'Processing',    icon: '📦' },
  shipped:    { label: 'Shipped',       icon: '🚚' },
  delivered:  { label: 'Delivered',     icon: '🎉' },
};

function getTrackingUrl(carrier, number) {
  if (!number) return null;
  const c = (carrier || '').toLowerCase();
  if (c === 'usps') return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${number}`;
  if (c === 'fedex') return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${number}`;
  if (c === 'ups') return `https://www.ups.com/track?tracknum=${number}`;
  if (number.startsWith('1Z')) return `https://www.ups.com/track?tracknum=${number}`;
  return `https://www.google.com/search?q=track+package+${number}`;
}

export default function TrackOrder() {
  const [form, setForm] = useState({ order_id: '', email: '' });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!form.order_id || !form.email) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const r = await api.get(`/orders/track/${form.order_id}?email=${encodeURIComponent(form.email)}`);
      setOrder(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Order not found. Please check your order number and email.');
    } finally {
      setLoading(false);
    }
  };

  const isCancelled = order?.status === 'cancelled';
  const currentStep = order && !isCancelled ? STEPS.indexOf(order.status) : -1;
  const shippingAddr = order?.shipping_address ? JSON.parse(order.shipping_address) : null;

  return (
    <div className="container" style={{ maxWidth: 700 }}>
      <h1 style={{ marginBottom: 8 }}>Track Your Order</h1>
      <p style={{ color: '#6b7280', marginBottom: 28 }}>Enter your order number and email address to track your order.</p>

      <div style={{ background: 'white', padding: 28, borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 28 }}>
        <form onSubmit={handleTrack} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Order Number</label>
            <input
              value={form.order_id}
              onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))}
              placeholder="e.g. 42"
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              required
            />
          </div>
          <div style={{ flex: '2 1 220px' }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              required
            />
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading} style={{ whiteSpace: 'nowrap' }}>
            {loading ? 'Looking up...' : 'Track Order'}
          </button>
        </form>
        {error && <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 14 }}>{error}</div>}
      </div>

      {order && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ margin: 0 }}>Order #{order.id}</h2>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Placed {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>

          {/* Tracking timeline */}
          {!isCancelled ? (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: '28px 24px', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 20, left: '10%', right: '10%', height: 3, background: '#e5e7eb', zIndex: 0 }} />
                <div style={{ position: 'absolute', top: 20, left: '10%', height: 3, width: currentStep <= 0 ? '0%' : `${(currentStep / (STEPS.length - 1)) * 80}%`, background: 'var(--primary)', zIndex: 1, transition: 'width 0.4s ease' }} />
                {STEPS.map((step, i) => {
                  const done = i <= currentStep;
                  const active = i === currentStep;
                  return (
                    <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, flex: 1 }}>
                      <div style={{ width: 42, height: 42, borderRadius: '50%', background: done ? 'var(--primary)' : '#f3f4f6', border: active ? '3px solid var(--primary)' : done ? 'none' : '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, marginBottom: 10, boxShadow: active ? '0 0 0 4px rgba(79,70,229,0.15)' : 'none' }}>
                        {done ? (active ? STEP_LABELS[step].icon : '✓') : STEP_LABELS[step].icon}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: done ? 700 : 400, color: done ? 'var(--primary)' : '#9ca3af', textAlign: 'center' }}>{STEP_LABELS[step].label}</span>
                    </div>
                  );
                })}
              </div>

              {order.tracking_number && (
                <div style={{ marginTop: 24, padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>🚚 Tracking Information</div>
                  <div style={{ fontSize: 14 }}>
                    {order.tracking_carrier && <strong style={{ marginRight: 8 }}>{order.tracking_carrier}</strong>}
                    <span style={{ fontFamily: 'monospace' }}>{order.tracking_number}</span>
                  </div>
                  <a href={getTrackingUrl(order.tracking_carrier, order.tracking_number)} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary" style={{ marginTop: 10, display: 'inline-flex' }}>
                    Track Package →
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: '#fee2e2', borderRadius: 12, padding: '16px 24px', marginBottom: 20, color: '#991b1b', fontWeight: 600 }}>
              ❌ This order has been cancelled.
            </div>
          )}

          {/* Items + Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 16, alignItems: 'start' }}>
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
              <h3 style={{ marginBottom: 14 }}>Items</h3>
              {order.items?.map(item => {
                const img = getImageUrl(item.image_url);
                return (
                  <div key={item.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                    {img ? <img src={img} alt={item.product_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6 }} /> : <div style={{ width: 56, height: 56, background: '#f3f4f6', borderRadius: 6 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{item.product_name}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>Qty: {item.quantity} × ${item.price.toFixed(2)}</div>
                    </div>
                    <div style={{ fontWeight: 700 }}>${item.total.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
              <h3 style={{ marginBottom: 14 }}>Summary</h3>
              {[['Subtotal', `$${order.subtotal?.toFixed(2)}`], ['Shipping', order.shipping === 0 ? 'FREE' : `$${order.shipping?.toFixed(2)}`], ['Tax', `$${order.tax?.toFixed(2)}`]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}><span style={{ color: '#6b7280' }}>{l}</span><span>{v}</span></div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 16, borderTop: '1px solid #e5e7eb', paddingTop: 10, marginTop: 4 }}>
                <span>Total</span><span>${order.total?.toFixed(2)}</span>
              </div>
              {shippingAddr && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f3f4f6', fontSize: 13, color: '#374151', lineHeight: 1.8 }}>
                  <strong>Ship to:</strong><br />
                  {shippingAddr.first_name} {shippingAddr.last_name}<br />
                  {shippingAddr.address}{shippingAddr.address2 ? `, ${shippingAddr.address2}` : ''}<br />
                  {shippingAddr.city}, {shippingAddr.state} {shippingAddr.zip}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
